import { createContext, useEffect, useReducer } from 'react';

import user from 'services/user';
import stats from 'services/stats';

import { useWallet } from './WalletContext';

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const POINT_VALUES_CACHE_KEY = 'holderPointValues';
const POINT_VALUES_CACHE_TIME_KEY = 'holderPointValues_time';

const initialState = {
  data: [],
  publicPointValues: null, // Public point values for all users
  tokenLeaderboard: [],
  tokenLeaderboardCount: 0,
  nftLeaderboard: [],
  nftLeaderboardCount: 0,
  loading: false,
};

const ACTIONS = {
  SET_DATA: 'SET_DATA',
  SET_PUBLIC_POINT_VALUES: 'SET_PUBLIC_POINT_VALUES',
  SET_LOADING: 'SET_LOADING',
  SET_TOKEN_LEADERBOARD: 'SET_TOKEN_LEADERBOARD',
  SET_TOKEN_COUNT: 'SET_TOKEN_COUNT',
  SET_NFT_LEADERBOARD: 'SET_NFT_LEADERBOARD',
  SET_NFT_COUNT: 'SET_NFT_COUNT',
};

const HodlerAirdropReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_DATA: {
      return {
        ...state,
        data: action.payload,
      };
    }
    case ACTIONS.SET_PUBLIC_POINT_VALUES: {
      return {
        ...state,
        publicPointValues: action.payload,
      };
    }
    case ACTIONS.SET_LOADING: {
      return {
        ...state,
        loading: action.payload,
      };
    }
    case ACTIONS.SET_TOKEN_LEADERBOARD: {
      return {
        ...state,
        tokenLeaderboard: action.payload,
      };
    }
    case ACTIONS.SET_TOKEN_COUNT: {
      return {
        ...state,
        tokenLeaderboardCount: action.payload,
      };
    }
    case ACTIONS.SET_NFT_LEADERBOARD: {
      return {
        ...state,
        nftLeaderboard: action.payload,
      };
    }
    case ACTIONS.SET_NFT_COUNT: {
      return {
        ...state,
        nftLeaderboardCount: action.payload,
      };
    }
  }

  throw Error('Unknown action: ' + action.type);
};

export const HodlerAirdropContext = createContext(null);

export const HodlerAirdropProvider = ({ children }) => {
  const [state, dispatch] = useReducer(HodlerAirdropReducer, initialState);
  const { wallet, isAuthenticated } = useWallet();

  const fetchHodlerData = async () => {
    setLoading(true);
    try {
      const data = await user.getHodlerBalance();
      setData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch public point values with caching (no auth required)
  const fetchPublicPointValues = async () => {
    try {
      // Check cache first
      const cached = localStorage.getItem(POINT_VALUES_CACHE_KEY);
      const cacheTime = localStorage.getItem(POINT_VALUES_CACHE_TIME_KEY);

      if (cached && cacheTime && Date.now() - parseInt(cacheTime) < CACHE_DURATION) {
        const data = JSON.parse(cached);
        setPublicPointValues(data);
        return data;
      }

      // Fetch fresh data
      const data = await stats.getHolderPointValues();

      // Cache the data
      localStorage.setItem(POINT_VALUES_CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(POINT_VALUES_CACHE_TIME_KEY, Date.now().toString());

      setPublicPointValues(data);
      console.log('✅ Public point values set');
      return data;
    } catch (error) {
      console.error('Failed to fetch public point values:', error);
      // Try to use cached data even if expired
      const cached = localStorage.getItem(POINT_VALUES_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        setPublicPointValues(data);
        return data;
      }
    }
  };

  const fetchTokenHodlerLeaderboard = async () => {
    setLoading(true);
    const result = await stats.getTokenHodlerLeaderboard({ skip: 0, limit: 50 });
    setTokenLeaderboardCount(result.count);
    setTokenLeaderboard(result.data);
    setLoading(false);
  };

  const loadMoreTokenHodler = async () => {
    if (!state.loading) {
      setLoading(true);
      const result = await stats.getTokenHodlerLeaderboard({
        limit: 50,
        skip: state.tokenLeaderboard.length,
      });
      setTokenLeaderboard([...state.tokenLeaderboard, ...result.data]);
      setLoading(false);
    }
  };

  const fetchNFTHodlerLeaderboard = async () => {
    setLoading(true);
    const result = await stats.getNftHodlerLeaderboard({ skip: 0, limit: 50 });
    setNftLeaderboardCount(result.count);
    setNftLeaderboard(result.data);
    setLoading(false);
  };

  const loadMoreNFTHodler = async () => {
    if (!state.loading) {
      setLoading(true);
      const result = await stats.getNftHodlerLeaderboard({
        limit: 50,
        skip: state.nftLeaderboard.length,
      });
      setNftLeaderboard([...state.nftLeaderboard, ...result.data]);
      setLoading(false);
    }
  };

  const setLoading = (loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
  };

  const setData = (data) => {
    dispatch({ type: ACTIONS.SET_DATA, payload: data });
  };

  const setPublicPointValues = (data) => {
    dispatch({ type: ACTIONS.SET_PUBLIC_POINT_VALUES, payload: data });
  };

  const setTokenLeaderboard = (loading) => {
    dispatch({ type: ACTIONS.SET_TOKEN_LEADERBOARD, payload: loading });
  };

  const setTokenLeaderboardCount = (loading) => {
    dispatch({ type: ACTIONS.SET_TOKEN_COUNT, payload: loading });
  };

  const setNftLeaderboard = (loading) => {
    dispatch({ type: ACTIONS.SET_NFT_LEADERBOARD, payload: loading });
  };

  const setNftLeaderboardCount = (loading) => {
    dispatch({ type: ACTIONS.SET_NFT_COUNT, payload: loading });
  };

  useEffect(() => {
    // Always fetch public point values for display
    fetchPublicPointValues();

    // Fetch user-specific data if authenticated
    if (wallet && isAuthenticated) {
      fetchHodlerData();
    }
  }, [wallet, isAuthenticated]);

  useEffect(() => {
    fetchTokenHodlerLeaderboard();
    fetchNFTHodlerLeaderboard();
  }, []);

  return (
    <HodlerAirdropContext.Provider
      value={{
        state,
        dispatch,
        loadMoreTokenHodler,
        loadMoreNFTHodler,
        fetchPublicPointValues,
      }}
    >
      {children}
    </HodlerAirdropContext.Provider>
  );
};
